// pages/profile.tsx or app/profile.tsx
"use client";
import profile from "@/assets/icons/profile.svg";
import Image from "next/image";
import { useEffect, useState, useRef } from "react";
import Cookies from "js-cookie";
import Link from "next/link";
import arrowDown from "@/assets/icons/arrow-down.svg";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import InvoiceIcon from "../ui/icons/Invoice";
import { X } from "lucide-react";
import { clearSessionCookies } from "@/utils/cart";
import CartCar from "../shared/CartCar";
import { fetchInit } from "@/utils/loaders";
import { useLanguage } from "@/contexts/LanguageContext";

const MobileUserMenu = ({ IsAppOpen, showStore: showStoreProp }: any) => {
  const profileCookie = Cookies.get("profile");

  const profil =
    profileCookie && profileCookie !== "undefined"
      ? JSON.parse(profileCookie)
      : null;
  const [showModal, setShowModal] = useState(false);
  const [storeStatus, setStoreStatus] = useState<number>(showStoreProp ?? 0);
  const { language } = useLanguage();
  const isRTL = language === "ar";
  const t = useTranslations();
  const menuRef = useRef<HTMLDivElement>(null);

  const pathname = usePathname();

  useEffect(() => {
    if (typeof showStoreProp !== "undefined") {
      setStoreStatus(Number(showStoreProp));
    }
  }, [showStoreProp]);

  useEffect(() => {
    const checkStoreStatus = async () => {
      try {
        const response = await fetchInit();
        if (
          response &&
          response.data &&
          typeof response.data.show_store !== "undefined"
        ) {
          setStoreStatus(Number(response.data.show_store));
        } else if (response && typeof response.show_store !== "undefined") {
          setStoreStatus(Number(response.show_store));
        }
      } catch (error) {
        console.error("Error checking store status:", error);
      }
    };
    checkStoreStatus();
  }, []);

  useEffect(() => {
    setShowModal(false);
  }, [pathname]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowModal(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="flex w-fit h-fit items-center justify-center shrink-0" ref={menuRef}>
      <div className="flex gap-1 items-center justify-center rounded-full w-fit h-fit shrink-0">
        <svg
          onClick={() => setShowModal(!showModal)}
          className="cursor-pointer rounded-full md:w-[46px] md:h-[46px] w-[35px]"
          viewBox="0 0 55 55"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g filter="url(#filter0_f_110_189)">
            <circle cx="27.0474" cy="27.0474" r="22.5474" stroke="#D9AC4F" />
          </g>
          <circle cx="27.0474" cy="27.0474" r="22.5474" stroke="#D9AC4F" />
          <circle cx="27.0472" cy="27.0472" r="20.0412" fill="#111111" />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M22.5383 23.2899C22.5383 22.7306 22.6485 22.1768 22.8625 21.6601C23.0765 21.1434 23.3902 20.6739 23.7857 20.2785C24.1812 19.883 24.6506 19.5693 25.1673 19.3553C25.684 19.1413 26.2378 19.0311 26.7971 19.0311C27.3564 19.0311 27.9101 19.1413 28.4268 19.3553C28.9435 19.5693 29.413 19.883 29.8085 20.2785C30.2039 20.6739 30.5176 21.1434 30.7317 21.6601C30.9457 22.1768 31.0558 22.7306 31.0558 23.2899C31.0558 24.4194 30.6072 25.5026 29.8085 26.3013C29.0098 27.0999 27.9266 27.5486 26.7971 27.5486C25.6676 27.5486 24.5844 27.0999 23.7857 26.3013C22.987 25.5026 22.5383 24.4194 22.5383 23.2899ZM26.7971 20.5342C26.0662 20.5342 25.3653 20.8245 24.8485 21.3413C24.3318 21.8581 24.0414 22.559 24.0414 23.2899C24.0414 24.0207 24.3318 24.7216 24.8485 25.2384C25.3653 25.7552 26.0662 26.0455 26.7971 26.0455C27.5279 26.0455 28.2288 25.7552 28.7456 25.2384C29.2624 24.7216 29.5528 24.0207 29.5528 23.2899C29.5528 22.559 29.2624 21.8581 28.7456 21.3413C28.2288 20.8245 27.5279 20.5342 26.7971 20.5342ZM22.7889 30.5548C22.1909 30.5548 21.6174 30.7923 21.1946 31.2152C20.7718 31.638 20.5342 32.2115 20.5342 32.8094V33.9999C20.5342 34.0179 20.5472 34.034 20.5653 34.037C24.6928 34.7103 28.9024 34.7103 33.0289 34.037C33.0374 34.035 33.045 34.0303 33.0506 34.0236C33.0563 34.0169 33.0595 34.0086 33.06 33.9999V32.8094C33.06 32.2115 32.8224 31.638 32.3996 31.2152C31.9768 30.7923 31.4033 30.5548 30.8053 30.5548H30.4646C30.4378 30.5553 30.4112 30.5593 30.3855 30.5668L29.5187 30.8504C27.7502 31.4279 25.8439 31.4279 24.0755 30.8504L23.2077 30.5668C23.1826 30.5595 23.1567 30.5554 23.1306 30.5548H22.7889ZM19.0311 32.8094C19.0311 31.8128 19.427 30.857 20.1317 30.1523C20.8364 29.4476 21.7922 29.0517 22.7889 29.0517H23.1296C23.3166 29.0524 23.4983 29.0811 23.6747 29.1379L24.5425 29.4215C26.0075 29.8998 27.5867 29.8998 29.0517 29.4215L29.9195 29.1379C30.0949 29.0808 30.2792 29.0517 30.4636 29.0517H30.8053C31.8019 29.0517 32.7577 29.4476 33.4624 30.1523C34.1671 30.857 34.563 31.8128 34.563 32.8094V33.9999C34.563 34.7554 34.0159 35.3988 33.2704 35.52C28.9832 36.2198 24.611 36.2198 20.3238 35.52C19.9632 35.4607 19.6354 35.2754 19.3987 34.997C19.162 34.7186 19.0317 34.3653 19.0311 33.9999V32.8094Z"
            fill="white"
          />
          <defs>
            <filter
              id="filter0_f_110_189"
              x="0"
              y="0"
              width="54.0947"
              height="54.0947"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="BackgroundImageFix"
                result="shape"
              />
              <feGaussianBlur
                stdDeviation="2"
                result="effect1_foregroundBlur_110_189"
              />
            </filter>
          </defs>
        </svg>
        <Image
          src={arrowDown}
          alt=""
          className="me-1 cursor-pointer"
          onClick={() => setShowModal(!showModal)}
        />
      </div>

      {showModal && (
        <div
          className={`glass-effect w-full h-screen overflow-y-auto flex flex-col px-5 py-6 fixed top-0 right-0  z-100 shadow-[0px_0px_12px_0px_rgba(0,0,0,0.12)] rounded-b-[20px]`}
        >
          <button
            onClick={() => setShowModal(false)}
            className="absolute top-5 rtl:left-5 ltr:right-5 bg-[#d9ac4f]  text-[#D1D3D4] text-[20px] border  border-[#d9ac4f] rounded-full w-[20px] h-[20px] flex items-center justify-center"
          >
            {" "}
            <X className="w-5 h-5 " />
          </button>
          <div className="flex items-center space-x-4 mb-3 ">
            <Image
              src={profil?.image || profile}
              alt=""
              width={89}
              height={89}
              className="rounded-full w-[89px] h-[89px]"
            />
            <div>
              <h2 className="text-xl font-bold text-[#D1D3D4]">
                {profil?.name}
              </h2>
              <Link
                href={"/user/profile"}
                className="text-sm font-medium text-[#D1D3D4] flex gap-2 items-center"
              >
                {t("mobileUserMenu.profile")}
                <svg
                  className={!isRTL ? "rotate-180" : ""}
                  width="15"
                  height="10"
                  viewBox="0 0 15 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 5H14M1 5L5 1M1 5L5 9"
                    stroke="#d9ac4f"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            </div>
          </div>
          {(() => {
            const isActive = (path: string) => pathname === path;
            const getLinkClass = (path: string) =>
              `rounded-[12px] px-6 py-3 flex gap-3 items-center mb-[10px] transition-all duration-300 ${
                isActive(path)
                  ? "bg-[#d9ac4f1F] border-2 border-[#D4AF37] shadow-[0_0_12px_#d9ac4f]"
                  : "bg-[#d9ac4f0F] border border-transparent"
              }`;

            return (
              <>
                <Link
                  href={"/user/tickets"}
                  className={getLinkClass("/user/tickets")}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 21 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.5995 16.9092L14.8353 16.1488C14.7342 16.0491 14.6148 15.9992 14.4772 15.9992C14.3387 15.9992 14.2193 16.0491 14.119 16.1488C14.0187 16.2477 13.9685 16.3648 13.9685 16.5C13.9685 16.6352 14.0187 16.7527 14.119 16.8524L15.1223 17.8493C15.267 17.9938 15.4299 18.0661 15.6112 18.0661C15.7924 18.0661 15.9553 17.9938 16.1 17.8493L18.5477 15.4532C18.648 15.3536 18.7021 15.2388 18.7098 15.109C18.7176 14.9784 18.6636 14.8559 18.5477 14.7415C18.4473 14.6418 18.3244 14.592 18.179 14.592C18.0336 14.592 17.9103 14.6422 17.8092 14.7427L15.5995 16.9092ZM14.8528 5.48416C15.0185 5.48416 15.1569 5.4289 15.2682 5.31839C15.3794 5.20788 15.4354 5.06994 15.4362 4.90456C15.4369 4.73918 15.3809 4.60124 15.2682 4.49073C15.1554 4.38022 15.0169 4.32496 14.8528 4.32496H3.81383C3.64817 4.32496 3.50933 4.38022 3.39733 4.49073C3.28533 4.60124 3.22972 4.73918 3.2305 4.90456C3.23128 5.06994 3.28689 5.20788 3.39733 5.31839C3.50778 5.4289 3.64661 5.48416 3.81383 5.48416H14.8528ZM16.3333 21C15.0337 21 13.9312 20.5498 13.0258 19.6495C12.1197 18.75 11.6667 17.6546 11.6667 16.3632C11.6667 15.0719 12.1197 13.976 13.0258 13.0757C13.9319 12.1754 15.0344 11.7257 16.3333 11.7264C17.6322 11.7272 18.7351 12.177 19.642 13.0757C20.5489 13.9745 21.0016 15.0703 21 16.3632C21 17.6538 20.5473 18.7492 19.642 19.6495C18.7351 20.5498 17.6322 21 16.3333 21ZM0 19.3818V1.87326C0 1.35394 0.183556 0.911901 0.550667 0.547141C0.917778 0.18238 1.36267 0 1.88533 0H16.7825C17.3036 0 17.7481 0.18238 18.116 0.547141C18.4831 0.911901 18.6667 1.35394 18.6667 1.87326V7.80255C18.6667 8.04212 18.5679 8.23725 18.3703 8.38794C18.1728 8.53864 17.9538 8.58771 17.7135 8.53516C17.1092 8.43006 16.5064 8.38949 15.9052 8.41345C15.3032 8.43663 14.7054 8.53014 14.112 8.69397H3.815C3.64933 8.69397 3.5105 8.74923 3.3985 8.85974C3.2865 8.97025 3.23089 9.10819 3.23167 9.27357C3.23244 9.43895 3.28806 9.57689 3.3985 9.6874C3.50894 9.79791 3.64778 9.85317 3.815 9.85317H11.7623C11.1774 10.2558 10.6618 10.7284 10.2153 11.2709C9.76889 11.8134 9.401 12.4107 9.11167 13.063H3.815C3.64933 13.063 3.5105 13.1182 3.3985 13.2287C3.2865 13.3393 3.23089 13.4772 3.23167 13.6426C3.23244 13.808 3.28806 13.9459 3.3985 14.0564C3.50894 14.1669 3.64778 14.2222 3.815 14.2222H8.72666C8.64266 14.5344 8.57733 14.8493 8.53067 15.1669C8.484 15.4845 8.46028 15.8161 8.4595 16.1615C8.4595 16.5301 8.48944 16.9003 8.54933 17.272C8.60922 17.643 8.687 18.0108 8.78267 18.3756C8.83478 18.5139 8.79978 18.6271 8.67767 18.7152C8.55478 18.8033 8.43811 18.8072 8.32766 18.7268L8.03717 18.5298C7.95083 18.4726 7.85983 18.444 7.76417 18.444C7.6685 18.444 7.57711 18.4722 7.49 18.5286L6.46683 19.2334C6.3805 19.2898 6.2895 19.318 6.19383 19.318C6.09817 19.318 6.00678 19.2898 5.91967 19.2334L4.8965 18.5286C4.80939 18.4722 4.718 18.444 4.62233 18.444C4.52667 18.444 4.43567 18.4722 4.34933 18.5286L3.32617 19.2334C3.23983 19.2906 3.14883 19.3192 3.05317 19.3192C2.9575 19.3192 2.86611 19.2906 2.779 19.2334L1.75583 18.5298C1.66872 18.4733 1.57733 18.4451 1.48167 18.4451C1.386 18.4451 1.295 18.4733 1.20867 18.5298L0.2975 19.2334C0.252389 19.2635 0.153611 19.3138 0.00116658 19.3841"
                      fill="#d9ac4f"
                    />
                  </svg>
                  <span className="text-[#D1D3D4] text-[18px] font-medium">
                    {t("mobileUserMenu.myOrders")}
                  </span>
                  <svg
                    className={`ms-auto ${!isRTL ? "rotate-180" : ""}`}
                    width="8"
                    height="13"
                    viewBox="0 0 8 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0.318083 5.77789L6.46383 0L8 1.44422L2.62233 6.5L8 11.5558L6.46383 13L0.318083 7.22211C0.114415 7.03057 0 6.77083 0 6.5C0 6.22917 0.114415 5.96943 0.318083 5.77789Z"
                      fill="#d9ac4f"
                    />
                  </svg>
                </Link>
                {storeStatus === 1 && (
                  <Link
                    href={"/user/cart"}
                    className={getLinkClass("/user/cart")}
                  >
                    <CartCar color="#d9ac4f" />
                    <span className="text-[#D1D3D4] text-[18px] font-medium">
                      {t("mobileUserMenu.cart")}
                    </span>
                    <svg
                      className={`ms-auto ${!isRTL ? "rotate-180" : ""}`}
                      width="8"
                      height="13"
                      viewBox="0 0 8 13"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0.318083 5.77789L6.46383 0L8 1.44422L2.62233 6.5L8 11.5558L6.46383 13L0.318083 7.22211C0.114415 7.03057 0 6.77083 0 6.5C0 6.22917 0.114415 5.96943 0.318083 5.77789Z"
                        fill="#d9ac4f"
                      />
                    </svg>
                  </Link>
                )}
                <Link
                  href={"/user/wallet"}
                  className={getLinkClass("/user/wallet")}
                >
                  <svg
                    width="20"
                    height="18"
                    viewBox="0 0 20 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M2.75 3.24805C2.02065 3.24805 1.32118 3.53778 0.805456 4.0535C0.289731 4.56923 0 5.2687 0 5.99805V14.998C0 15.7274 0.289731 16.4269 0.805456 16.9426C1.32118 17.4583 2.02065 17.748 2.75 17.748H16.75C17.4793 17.748 18.1788 17.4583 18.6945 16.9426C19.2103 16.4269 19.5 15.7274 19.5 14.998V5.99805C19.5 5.2687 19.2103 4.56923 18.6945 4.0535C18.1788 3.53778 17.4793 3.24805 16.75 3.24805H2.75ZM14.25 9.24805C13.9185 9.24805 13.6005 9.37974 13.3661 9.61416C13.1317 9.84858 13 10.1665 13 10.498C13 10.8296 13.1317 11.1475 13.3661 11.3819C13.6005 11.6164 13.9185 11.748 14.25 11.748C14.5815 11.748 14.8995 11.6164 15.1339 11.3819C15.3683 11.1475 15.5 10.8296 15.5 10.498C15.5 10.1665 15.3683 9.84858 15.1339 9.61416C14.8995 9.37974 14.5815 9.24805 14.25 9.24805Z"
                      fill="#d9ac4f"
                    />
                    <path
                      d="M14.235 0.0674436C14.5311 -0.011458 14.8413 -0.0212737 15.1418 0.0387537C15.4423 0.0987812 15.7249 0.227047 15.968 0.413651C16.211 0.600255 16.4079 0.840207 16.5435 1.11499C16.6791 1.38977 16.7498 1.69203 16.75 1.99844H6.75L14.235 0.0674436Z"
                      fill="#d9ac4f"
                    />
                  </svg>
                  <span className="text-[#D1D3D4] text-[18px] font-medium">
                    {t("mobileUserMenu.wallet")}
                  </span>
                  <svg
                    className={`ms-auto ${!isRTL ? "rotate-180" : ""}`}
                    width="8"
                    height="13"
                    viewBox="0 0 8 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0.318083 5.77789L6.46383 0L8 1.44422L2.62233 6.5L8 11.5558L6.46383 13L0.318083 7.22211C0.114415 7.03057 0 6.77083 0 6.5C0 6.22917 0.114415 5.96943 0.318083 5.77789Z"
                      fill="#d9ac4f"
                    />
                  </svg>
                </Link>
                <Link
                  href={"/seller-home"}
                  className={getLinkClass("/seller-home")}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 19 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.7 0H1.9C1.39609 0 0.912816 0.210714 0.556497 0.585786C0.200178 0.960859 0 1.46957 0 2V6C0 6.53043 0.200178 7.03914 0.556497 7.41421C0.912816 7.78929 1.39609 8 1.9 8H5.7C6.20391 8 6.68718 7.78929 7.0435 7.41421C7.39982 7.03914 7.6 6.53043 7.6 6V2C7.6 1.46957 7.39982 0.960859 7.0435 0.585786C6.68718 0.210714 6.20391 0 5.7 0ZM17.1 12H13.3C12.7961 12 12.3128 12.2107 11.9565 12.5858C11.6002 12.9609 11.4 13.4696 11.4 14V18C11.4 18.5304 11.6002 19.0391 11.9565 19.4142C12.3128 19.7893 12.7961 20 13.3 20H17.1C17.6039 20 18.0872 19.7893 18.4435 19.4142C18.7998 19.0391 19 18.5304 19 18V14C19 13.4696 18.7998 12.9609 18.4435 12.5858C18.0872 12.2107 17.6039 12 17.1 12ZM13.9717 0.293C14.1352 0.465193 14.2335 0.694293 14.248 0.937319C14.2625 1.18034 14.1923 1.4206 14.0505 1.613L13.9717 1.707L12.7433 3H16.15C16.8769 2.99996 17.5764 3.29233 18.1053 3.81728C18.6343 4.34224 18.9526 5.06011 18.9952 5.824L19 6V9C18.9997 9.25488 18.907 9.50003 18.7408 9.68537C18.5746 9.8707 18.3474 9.98223 18.1057 9.99717C17.864 10.0121 17.6259 9.92933 17.4403 9.76573C17.2546 9.60214 17.1352 9.3701 17.1066 9.117L17.1 9V6C17.1 5.75507 17.0145 5.51866 16.8599 5.33563C16.7053 5.15259 16.4922 5.03566 16.2611 5.007L16.15 5H12.7442L13.9717 6.293C14.142 6.47296 14.2409 6.71443 14.2483 6.96838C14.2557 7.22233 14.1709 7.46971 14.0113 7.66028C13.8517 7.85084 13.6291 7.9703 13.3888 7.9944C13.1486 8.01849 12.9086 7.94541 12.7176 7.79L12.6283 7.707L9.77835 4.707C9.6699 4.59544 9.58898 4.45769 9.54263 4.30572C9.49627 4.15375 9.48587 3.99216 9.51235 3.835L9.54655 3.689L9.58455 3.585C9.63965 3.45767 9.71723 3.34633 9.8173 3.251L12.6283 0.293C12.8065 0.105529 13.0481 0.000213623 13.3 0.000213623C13.5519 0.000213623 13.7935 0.105529 13.9717 0.293ZM0.95 10C1.18269 10 1.40727 10.09 1.58115 10.2527C1.75504 10.4155 1.86613 10.6397 1.89335 10.883L1.9 11V14C1.90003 14.2449 1.98546 14.4813 2.14008 14.6644C2.2947 14.8474 2.50776 14.9643 2.73885 14.993L2.85 15H6.25575L5.02835 13.707C4.86478 13.5348 4.76652 13.3057 4.75201 13.0627C4.73749 12.8197 4.80771 12.5794 4.9495 12.387L5.02835 12.293C5.19193 12.1208 5.40958 12.0174 5.64045 12.0021C5.87133 11.9868 6.09957 12.0607 6.28235 12.21L6.37165 12.293L9.22165 15.293C9.3301 15.4046 9.41102 15.5423 9.45737 15.6943C9.50373 15.8462 9.51412 16.0078 9.48765 16.165L9.45345 16.311L9.41545 16.415C9.36086 16.5422 9.28158 16.656 9.1827 16.749L6.37165 19.707C6.20069 19.8863 5.97129 19.9905 5.73004 19.9982C5.48878 20.006 5.25377 19.9168 5.07273 19.7487C4.8917 19.5807 4.77821 19.3464 4.75532 19.0935C4.73243 18.8406 4.80186 18.588 4.9495 18.387L5.02835 18.293L6.2548 17H2.85C2.12305 17 1.42356 16.7077 0.894652 16.1827C0.365743 15.6578 0.0473979 14.9399 0.00475011 14.176L0 14V11C0 10.7348 0.100089 10.4804 0.278249 10.2929C0.456408 10.1054 0.698044 10 0.95 10Z"
                      fill="#d9ac4f"
                    />
                  </svg>

                  <span className="text-[#D1D3D4] text-[18px] font-medium">
                    {t("mobileUserMenu.sellTickets")}
                  </span>
                  <svg
                    className={`ms-auto ${!isRTL ? "rotate-180" : ""}`}
                    width="8"
                    height="13"
                    viewBox="0 0 8 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0.318083 5.77789L6.46383 0L8 1.44422L2.62233 6.5L8 11.5558L6.46383 13L0.318083 7.22211C0.114415 7.03057 0 6.77083 0 6.5C0 6.22917 0.114415 5.96943 0.318083 5.77789Z"
                      fill="#d9ac4f"
                    />
                  </svg>
                </Link>
                <p className="text-[#D1D3D4] font-bold mt-2 mb-5">
                  {t("mobileUserMenu.sellerDashboard")}
                </p>
                <Link
                  href={"/seller/tickets"}
                  className={getLinkClass("/seller/tickets")}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 26 26"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M25.6857 8.95988L23.2952 6.56929C23.1023 6.37901 22.8452 6.26791 22.5744 6.25788C22.3037 6.24785 22.039 6.33963 21.8326 6.51511C21.514 6.78637 21.105 6.92801 20.6869 6.91195C20.2687 6.8959 19.8718 6.72333 19.5749 6.42844C19.2801 6.13152 19.1077 5.73473 19.0916 5.31667C19.0756 4.8986 19.2171 4.48976 19.4882 4.17111C19.6637 3.96469 19.7554 3.70001 19.7454 3.42926C19.7354 3.15851 19.6243 2.90135 19.434 2.70847L17.0414 0.315172C16.8392 0.11335 16.5652 0 16.2795 0C15.9938 0 15.7198 0.11335 15.5176 0.315172L11.7069 4.1256C11.4716 4.36177 11.2942 4.64915 11.1885 4.96526C11.1679 5.02605 11.1336 5.08127 11.0882 5.12665C11.0428 5.17202 10.9876 5.20633 10.9268 5.22691C10.6106 5.33264 10.3232 5.51028 10.0872 5.74588L0.315155 15.5184C0.114344 15.7206 0 15.9947 0 16.2804C0 16.5661 0.113344 16.8401 0.315155 17.0423L2.70833 19.4329C2.9012 19.6232 3.15835 19.7343 3.42908 19.7443C3.69981 19.7543 3.96448 19.6625 4.17089 19.4871C4.4888 19.2136 4.8983 19.0704 5.31733 19.0861C5.73636 19.1019 6.13396 19.2754 6.43047 19.5719C6.72698 19.8684 6.90048 20.2661 6.91622 20.6851C6.93195 21.1042 6.78875 21.5137 6.51532 21.8316C6.33984 22.038 6.24807 22.3027 6.25809 22.5734C6.26812 22.8442 6.37922 23.1014 6.56948 23.2942L8.95995 25.6848C9.16215 25.8867 9.43616 26 9.72184 26C10.0075 26 10.2815 25.8867 10.4837 25.6848L20.2558 15.9123C20.4913 15.6764 20.6689 15.3892 20.7747 15.0731C20.7952 15.0122 20.8295 14.9568 20.875 14.9113C20.9205 14.8658 20.9759 14.8314 21.0369 14.811C21.3528 14.7052 21.64 14.5278 21.876 14.2925L25.6862 10.4816C25.8873 10.2794 26.0001 10.0058 26 9.72061C25.9999 9.43545 25.8869 9.16193 25.6857 8.95988ZM13.3151 7.35369C13.2346 7.4342 13.1391 7.49806 13.0339 7.54163C12.9288 7.5852 12.816 7.60763 12.7022 7.60763C12.5884 7.60763 12.4757 7.5852 12.3705 7.54163C12.2653 7.49806 12.1698 7.4342 12.0893 7.35369L11.4658 6.73018C11.3072 6.56681 11.2193 6.3476 11.221 6.11993C11.2227 5.89225 11.3139 5.6744 11.475 5.51344C11.636 5.35248 11.8539 5.26134 12.0815 5.25973C12.3092 5.25812 12.5284 5.34615 12.6916 5.50481L13.3151 6.12779C13.3956 6.20827 13.4595 6.30383 13.5031 6.409C13.5466 6.51418 13.569 6.6269 13.569 6.74074C13.569 6.85458 13.5466 6.9673 13.5031 7.07247C13.4595 7.17765 13.3956 7.2732 13.3151 7.35369ZM15.6986 9.73724C15.536 9.89967 15.3157 9.99091 15.0859 9.99091C14.8561 9.99091 14.6358 9.89967 14.4733 9.73724L13.8774 9.14135C13.7148 8.97879 13.6235 8.7583 13.6235 8.5284C13.6235 8.2985 13.7148 8.07802 13.8774 7.91545C14.04 7.75289 14.2604 7.66156 14.4903 7.66156C14.7202 7.66156 14.9407 7.75289 15.1032 7.91545L15.6991 8.51134C15.7799 8.59182 15.844 8.68745 15.8877 8.79275C15.9315 8.89806 15.954 9.01096 15.9541 9.12499C15.9542 9.23902 15.9317 9.35195 15.888 9.45729C15.8444 9.56263 15.7803 9.65832 15.6996 9.73887L15.6986 9.73724ZM18.082 12.1208C18.0015 12.2013 17.9059 12.2652 17.8008 12.3087C17.6956 12.3523 17.5829 12.3747 17.4691 12.3747C17.3552 12.3747 17.2425 12.3523 17.1373 12.3087C17.0322 12.2652 16.9366 12.2013 16.8561 12.1208L16.2603 11.5249C16.1017 11.3615 16.0138 11.1423 16.0155 10.9147C16.0172 10.687 16.1084 10.4691 16.2694 10.3082C16.4305 10.1472 16.6484 10.0561 16.876 10.0545C17.1037 10.0528 17.3228 10.1409 17.4861 10.2995L18.082 10.8954C18.163 10.9758 18.2273 11.0713 18.2713 11.1766C18.3153 11.2819 18.3381 11.3948 18.3383 11.5089C18.3386 11.623 18.3163 11.736 18.2728 11.8415C18.2293 11.947 18.1653 12.0428 18.0847 12.1235L18.082 12.1208ZM20.4887 14.5336C20.4082 14.6141 20.3127 14.678 20.2075 14.7215C20.1023 14.7651 19.9896 14.7875 19.8758 14.7875C19.7619 14.7875 19.6492 14.7651 19.5441 14.7215C19.4389 14.678 19.3433 14.6141 19.2629 14.5336L18.6432 13.9106C18.561 13.8304 18.4956 13.7346 18.4507 13.6288C18.4059 13.5231 18.3825 13.4095 18.3819 13.2946C18.3814 13.1797 18.4037 13.0659 18.4475 12.9597C18.4914 12.8535 18.5559 12.7571 18.6373 12.6761C18.7187 12.595 18.8154 12.531 18.9218 12.4877C19.0282 12.4444 19.1421 12.4226 19.257 12.4237C19.3718 12.4248 19.4853 12.4488 19.5909 12.4941C19.6964 12.5395 19.7919 12.6054 19.8717 12.688L20.4919 13.3104C20.5724 13.3909 20.6362 13.4865 20.6798 13.5917C20.7233 13.6969 20.7456 13.8096 20.7456 13.9235C20.7455 14.0373 20.7231 14.15 20.6795 14.2552C20.6358 14.3603 20.5719 14.4559 20.4914 14.5363L20.4887 14.5336Z"
                      fill="#d9ac4f"
                    />
                  </svg>

                  <span className="text-[#D1D3D4] text-[18px] font-medium">
                    {t("mobileUserMenu.tickets")}
                  </span>
                  <svg
                    className={`ms-auto ${!isRTL ? "rotate-180" : ""}`}
                    width="8"
                    height="13"
                    viewBox="0 0 8 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0.318083 5.77789L6.46383 0L8 1.44422L2.62233 6.5L8 11.5558L6.46383 13L0.318083 7.22211C0.114415 7.03057 0 6.77083 0 6.5C0 6.22917 0.114415 5.96943 0.318083 5.77789Z"
                      fill="#d9ac4f"
                    />
                  </svg>
                </Link>
                <Link
                  href={"/seller/invoices"}
                  className={getLinkClass("/seller/invoices")}
                >
                  <InvoiceIcon />

                  <span className="text-[#D1D3D4] text-[18px] font-medium">
                    {t("mobileUserMenu.invoices")}
                  </span>
                  <svg
                    className={`ms-auto ${!isRTL ? "rotate-180" : ""}`}
                    width="8"
                    height="13"
                    viewBox="0 0 8 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0.318083 5.77789L6.46383 0L8 1.44422L2.62233 6.5L8 11.5558L6.46383 13L0.318083 7.22211C0.114415 7.03057 0 6.77083 0 6.5C0 6.22917 0.114415 5.96943 0.318083 5.77789Z"
                      fill="#d9ac4f"
                    />
                  </svg>
                </Link>
                <Link
                  href={"/seller/sales"}
                  className={getLinkClass("/seller/sales")}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.21 6.66063e-05H6.75C4.9606 0.00271169 3.24525 0.714717 1.97995 1.98001C0.714653 3.2453 0.00264509 4.96065 0 6.75004V13.25C0.00264509 15.0394 0.714653 16.7548 1.97995 18.0201C3.24525 19.2854 4.9606 19.9974 6.75 20H13.25C15.0394 19.9974 16.7548 19.2854 18.0201 18.0201C19.2853 16.7548 19.9974 15.0394 20 13.25V6.75004C19.9987 5.86062 19.8219 4.9802 19.4797 4.15924C19.1375 3.33827 18.6367 2.59291 18.0059 1.96585C17.3751 1.3388 16.6268 0.84238 15.8038 0.505053C14.9809 0.167725 14.0994 -0.00388149 13.21 6.66063e-05ZM15.1 10.69H14.96C14.7839 10.6884 14.6139 10.6257 14.4789 10.5126C14.3439 10.3995 14.2524 10.2431 14.22 10.07L14.04 9.07004L12.73 11.07C12.4832 11.4374 12.1035 11.6948 11.6709 11.788C11.2382 11.8813 10.7862 11.8031 10.41 11.57L8.14 10.13C8.09818 10.1138 8.05182 10.1138 8.01 10.13C7.95779 10.1385 7.91105 10.1672 7.88 10.21L5.56 13.3C5.4891 13.392 5.39829 13.4668 5.29439 13.5188C5.19048 13.5707 5.07616 13.5985 4.96 13.6C4.7976 13.6008 4.63948 13.548 4.51 13.45C4.36478 13.3321 4.26888 13.1641 4.24113 12.9791C4.21338 12.7941 4.25578 12.6054 4.36 12.45L6.68 9.36003C6.93539 9.0227 7.30677 8.79203 7.72236 8.71261C8.13794 8.63319 8.56821 8.71065 8.93 8.93004L11.21 10.37C11.2566 10.3932 11.3096 10.3998 11.3605 10.3889C11.4113 10.378 11.457 10.3502 11.49 10.31L12.83 8.31004L11.75 8.46004C11.6528 8.47842 11.553 8.47749 11.4562 8.45728C11.3593 8.43708 11.2675 8.39801 11.1857 8.34229C11.104 8.28658 11.0341 8.21531 10.9799 8.13257C10.9257 8.04982 10.8884 7.95722 10.87 7.86004C10.8516 7.76286 10.8526 7.66301 10.8728 7.5662C10.893 7.46938 10.932 7.37749 10.9877 7.29578C11.0435 7.21406 11.1147 7.14412 11.1975 7.08995C11.2802 7.03577 11.3728 6.99843 11.47 6.98004L14.23 6.47005H14.59C14.6159 6.4609 14.6441 6.4609 14.67 6.47005L14.82 6.55005L14.97 6.67004C15.006 6.71338 15.036 6.76004 15.06 6.81004C15.088 6.85646 15.1083 6.90712 15.12 6.96004L15.64 9.76003C15.6671 9.85657 15.6745 9.95755 15.6619 10.057C15.6492 10.1565 15.6168 10.2524 15.5664 10.3391C15.5161 10.4258 15.4489 10.5015 15.3688 10.5618C15.2886 10.6221 15.1973 10.6657 15.1 10.69Z"
                      fill="#d9ac4f"
                    />
                  </svg>

                  <span className="text-[#D1D3D4] text-[18px] font-medium">
                    {t("mobileUserMenu.mySales")}
                  </span>
                  <svg
                    className={`ms-auto ${!isRTL ? "rotate-180" : ""}`}
                    width="8"
                    height="13"
                    viewBox="0 0 8 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0.318083 5.77789L6.46383 0L8 1.44422L2.62233 6.5L8 11.5558L6.46383 13L0.318083 7.22211C0.114415 7.03057 0 6.77083 0 6.5C0 6.22917 0.114415 5.96943 0.318083 5.77789Z"
                      fill="#d9ac4f"
                    />
                  </svg>
                </Link>
                <Link
                  href={"/seller/wallet"}
                  className={getLinkClass("/seller/wallet")}
                >
                  <svg
                    width="20"
                    height="18"
                    viewBox="0 0 20 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M2.75 3.24805C2.02065 3.24805 1.32118 3.53778 0.805456 4.0535C0.289731 4.56923 0 5.2687 0 5.99805V14.998C0 15.7274 0.289731 16.4269 0.805456 16.9426C1.32118 17.4583 2.02065 17.748 2.75 17.748H16.75C17.4793 17.748 18.1788 17.4583 18.6945 16.9426C19.2103 16.4269 19.5 15.7274 19.5 14.998V5.99805C19.5 5.2687 19.2103 4.56923 18.6945 4.0535C18.1788 3.53778 17.4793 3.24805 16.75 3.24805H2.75ZM14.25 9.24805C13.9185 9.24805 13.6005 9.37974 13.3661 9.61416C13.1317 9.84858 13 10.1665 13 10.498C13 10.8296 13.1317 11.1475 13.3661 11.3819C13.6005 11.6164 13.9185 11.748 14.25 11.748C14.5815 11.748 14.8995 11.6164 15.1339 11.3819C15.3683 11.1475 15.5 10.8296 15.5 10.498C15.5 10.1665 15.3683 9.84858 15.1339 9.61416C14.8995 9.37974 14.5815 9.24805 14.25 9.24805Z"
                      fill="#d9ac4f"
                    />
                    <path
                      d="M14.235 0.0674436C14.5311 -0.011458 14.8413 -0.0212737 15.1418 0.0387537C15.4423 0.0987812 15.7249 0.227047 15.968 0.413651C16.211 0.600255 16.4079 0.840207 16.5435 1.11499C16.6791 1.38977 16.7498 1.69203 16.75 1.99844H6.75L14.235 0.0674436Z"
                      fill="#d9ac4f"
                    />
                  </svg>

                  <span className="text-[#D1D3D4] text-[18px] font-medium">
                    {t("mobileUserMenu.wallet")}
                  </span>
                  <svg
                    className={`ms-auto ${!isRTL ? "rotate-180" : ""}`}
                    width="8"
                    height="13"
                    viewBox="0 0 8 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0.318083 5.77789L6.46383 0L8 1.44422L2.62233 6.5L8 11.5558L6.46383 13L0.318083 7.22211C0.114415 7.03057 0 6.77083 0 6.5C0 6.22917 0.114415 5.96943 0.318083 5.77789Z"
                      fill="#d9ac4f"
                    />
                  </svg>
                </Link>
              </>
            );
          })()}
          <Link
            href={"/business/register"}
            className="bg-[#d9ac4f]  rounded-[12px] pr-10 pl-6 py-4 flex gap-3 items-center mb-[18px]"
          >
            <span className="text-[#D1D3D4] text-[18px] font-medium">
              {t("mobileUserMenu.goldenTicketForBusiness")}
            </span>
            <svg
              className={`ms-auto ${!isRTL ? "rotate-180" : ""}`}
              width="8"
              height="13"
              viewBox="0 0 8 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.318083 5.77789L6.46383 0L8 1.44422L2.62233 6.5L8 11.5558L6.46383 13L0.318083 7.22211C0.114415 7.03057 0 6.77083 0 6.5C0 6.22917 0.114415 5.96943 0.318083 5.77789Z"
                fill="white"
              />
            </svg>
          </Link>

          <button
            onClick={() => {
              clearSessionCookies();
              window.location.href = "/";
            }}
            className="bg-[#F9511F33]  rounded-[12px] pr-10 pl-6 py-4 flex gap-3 items-center mb-[10px]"
          >
            <svg
              width="22"
              height="23"
              viewBox="0 0 22 23"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M3.47372 0C2.55243 0 1.66888 0.363481 1.01743 1.01048C0.365981 1.65748 0 2.535 0 3.45V19.55C0 20.465 0.365981 21.3425 1.01743 21.9895C1.66888 22.6365 2.55243 23 3.47372 23H10.4212C11.3425 23 12.226 22.6365 12.8775 21.9895C13.5289 21.3425 13.8949 20.465 13.8949 19.55V3.45C13.8949 2.535 13.5289 1.65748 12.8775 1.01048C12.226 0.363481 11.3425 0 10.4212 0H3.47372ZM15.3921 6.08695C15.6092 5.87136 15.9037 5.75025 16.2107 5.75025C16.5177 5.75025 16.8122 5.87136 17.0294 6.08695L21.661 10.6869C21.8781 10.9026 22 11.1951 22 11.5C22 11.8049 21.8781 12.0974 21.661 12.313L17.0294 16.913C16.811 17.1225 16.5185 17.2384 16.2149 17.2358C15.9113 17.2332 15.6209 17.1123 15.4062 16.899C15.1915 16.6858 15.0697 16.3974 15.0671 16.0959C15.0644 15.7943 15.1811 15.5038 15.3921 15.287L18.0471 12.65H8.10535C7.79826 12.65 7.50374 12.5288 7.28659 12.3132C7.06944 12.0975 6.94745 11.805 6.94745 11.5C6.94745 11.195 7.06944 10.9025 7.28659 10.6868C7.50374 10.4712 7.79826 10.35 8.10535 10.35H18.0471L15.3921 7.71305C15.175 7.49739 15.053 7.20494 15.053 6.9C15.053 6.59506 15.175 6.30261 15.3921 6.08695Z"
                fill="#F9511F"
              />
            </svg>

            <span className="text-[#D1D3D4] text-[18px] font-medium">
              {t("mobileUserMenu.logout")}
            </span>
            <svg
              className={`ms-auto ${!isRTL ? "rotate-180" : ""}`}
              width="8"
              height="13"
              viewBox="0 0 8 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0.318083 5.77789L6.46383 0L8 1.44422L2.62233 6.5L8 11.5558L6.46383 13L0.318083 7.22211C0.114415 7.03057 0 6.77083 0 6.5C0 6.22917 0.114415 5.96943 0.318083 5.77789Z"
                fill="#F9511F"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default MobileUserMenu;
